"use client";

/**
 * Hub sections for /web-index.
 *
 * WebIndexBento     — asymmetric routing grid: one 2x2 lead tile (the node CTA,
 *                     the page's primary action) plus capability tiles that
 *                     point at each child page. Patterned on the /design
 *                     "features-bento" variation.
 * WebIndexPipeline  — numbered vertical timeline of how a page becomes an
 *                     answer. Patterned on the /design "features-timeline"
 *                     variation.
 *
 * Both stay at the OUTCOME level — no queue, claim or protocol detail.
 */

import { useRef } from "react";
import Link from "next/link";
import { motion, useInView } from "motion/react";
import {
  AppWindow,
  ArrowRight,
  ArrowUpRight,
  Bug,
  Command,
  Container,
  Eye,
  FileSearch,
  HardDrive,
  Radio,
  Search,
  Server,
  Sparkles,
} from "lucide-react";

/* ──────────────── Bento — the four ways in ──────────────── */

/** The four install routes, named by WHO each is for.
 *
 *  macOS is on this list because it was the one missing: /indexer_macos has existed and shipped
 *  and nothing on this page linked to it, so the only way to find it was to guess the URL. The
 *  Docker tile's body even said "Windows, Linux or macOS", which quietly implied Docker was the
 *  only route a Mac had. */
const PLATFORMS = [
  { icon: HardDrive, name: "Indexer OS", who: "Partners, dedicated machines", href: "/indexer_os" },
  { icon: AppWindow, name: "Windows", who: "One-line installer", href: "/indexer_node" },
  { icon: Command, name: "macOS", who: "Apple silicon and Intel", href: "/indexer_macos" },
  { icon: Container, name: "Docker", who: "Any host you already run", href: "/indexer_docker" },
];

const TILES = [
  {
    icon: Bug,
    title: "The crawler",
    body: "How we read the web, and the contract we keep with site owners.",
    href: "/crawler",
  },
  {
    icon: HardDrive,
    title: "askFinz Indexer OS",
    body: "A custom Linux OS that turns a spare PC into a dedicated, self-managing node.",
    href: "/indexer_os",
  },
  {
    icon: Container,
    title: "askFinz Indexer for Docker",
    body: "One command on any Docker host — Windows, Linux or macOS.",
    href: "/indexer_docker",
  },
  {
    icon: Radio,
    title: "Live status",
    body: "The fleet as it runs right now — every machine, every page.",
    href: "https://askfinz.ai/indexing_in_progress",
    external: true,
  },
  {
    icon: Search,
    title: "What it powers",
    body: "The search engine this index answers from, for people and agents alike.",
    href: "/apps/search",
  },
];

export function WebIndexBento() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="border-b hairline">
      <div className="container-x py-14 md:py-20">
        <div className="flex items-baseline justify-between border-b hairline pb-4">
          <h2 className="text-sm tracking-[0.18em] uppercase text-foreground/65">
            The whole picture
          </h2>
          <span className="font-mono text-xs text-foreground/65">06 places to go</span>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 auto-rows-[minmax(150px,auto)] gap-4">
          {/* Lead 2x2 tile — the primary action on this page */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="md:col-span-2 md:row-span-2"
          >
            {/* NOT ONE BIG ANCHOR ANY MORE, and it cannot be. This tile offers four destinations —
                one per platform — and an anchor may not contain anchors, so wrapping the whole card
                in a Link to /indexer_node forced every route but that one to be prose. It also
                buried macOS entirely: the page existed and nothing on this page pointed at it.
                The heading carries the general link; each platform carries its own. */}
            <div className="group lift-card block h-full rounded-2xl border hairline-strong bg-card p-6 md:p-8 flex flex-col hover:hairline-strong">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-foreground text-background">
                  <Server className="h-4 w-4" />
                </span>
                <span className="kicker">Run a node</span>
              </div>
              <Link href="/indexer_node" className="mt-4 block">
                <h3 className="text-2xl md:text-3xl font-medium tracking-tight">
                  Give a machine{" "}
                  <span className="font-serif italic">something to read.</span>
                </h3>
              </Link>
              <p className="mt-2 max-w-md text-foreground/65">
                A computer you already own can take a share of the reading,
                using only what it can spare. Nothing reformatted, and one line
                to take it back off again.
              </p>

              {/* Four ways in, each its own link. The label says who it is for rather than what it
                  is called — someone on a Mac should not have to know the product name to find
                  their own route. */}
              <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {PLATFORMS.map((pl) => {
                  const PIcon = pl.icon;
                  return (
                    <Link
                      key={pl.href}
                      href={pl.href}
                      className="group/pf flex flex-col gap-1.5 rounded-xl border hairline bg-background/60 p-3 transition-colors hover:bg-foreground/[0.04] hover:hairline-strong"
                    >
                      <PIcon className="h-4 w-4 text-foreground/70 transition-colors group-hover/pf:text-foreground" />
                      <span className="text-[13px] font-medium leading-tight tracking-tight">{pl.name}</span>
                      <span className="text-[11px] leading-snug text-foreground/50">{pl.who}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Mini mock — the install line, verbatim */}
              <div className="mt-auto pt-6">
                <div className="rounded-xl border hairline bg-background/60 p-4">
                  <p className="font-mono text-[11px] text-foreground/45 mb-2">
                    windows · powershell
                  </p>
                  <code className="block overflow-x-auto whitespace-nowrap font-mono text-[12.5px] text-foreground/85">
                    irm https://get.askfinz.ai/node | iex
                  </code>
                </div>
                <Link
                  href="/indexer_node"
                  className="group/how mt-4 inline-flex items-center gap-1.5 text-sm font-medium"
                >
                  How it works
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/how:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </motion.div>

          {TILES.map((t, i) => {
            const Icon = t.icon;
            const inner = (
              <>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border hairline-strong text-foreground/80">
                  <Icon className="h-4 w-4" />
                </span>
                <h3 className="mt-3 text-[15px] font-medium tracking-tight inline-flex items-center gap-1.5">
                  {t.title}
                  {t.external ? (
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-40" />
                  ) : (
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-50 group-hover:translate-x-0 transition-all" />
                  )}
                </h3>
                <p className="mt-1.5 text-sm text-foreground/60 leading-relaxed">{t.body}</p>
              </>
            );
            const cls =
              "group lift-card h-full rounded-2xl border hairline-strong bg-card p-5 flex flex-col";
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.45, delay: 0.12 + i * 0.07 }}
              >
                {t.external ? (
                  <a href={t.href} target="_blank" rel="noopener noreferrer" className={cls}>
                    {inner}
                  </a>
                ) : (
                  <Link href={t.href} className={cls}>
                    {inner}
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Pipeline — how a page becomes an answer ──────────────── */

const STEPS = [
  {
    icon: Eye,
    title: "Seen",
    body: "A page is reached — by the crawler working through the web, or by the extension as somebody reads it.",
  },
  {
    icon: FileSearch,
    title: "Read properly",
    body: "Menus and clutter come off. Documents are read all the way through. Pages that only assemble in a browser get built first.",
  },
  {
    icon: Sparkles,
    title: "Understood",
    body: "What the page means is worked out and stored — not just the words on it, so a question can match an idea rather than a keyword.",
  },
  {
    icon: Search,
    title: "Answerable",
    body: "It becomes searchable immediately, and any answer drawn from it cites the page it came from.",
  },
];

function Step({
  step,
  index,
  last,
}: {
  step: (typeof STEPS)[number];
  index: number;
  last: boolean;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const Icon = step.icon;

  return (
    <div ref={ref} className="relative flex gap-5 pb-10 last:pb-0">
      {!last && (
        <span className="absolute left-[27px] top-14 bottom-0 w-px bg-foreground/12" aria-hidden />
      )}
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.4, delay: index * 0.08 }}
        className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-foreground text-background"
      >
        <Icon className="h-5 w-5" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.45, delay: 0.08 + index * 0.08 }}
        className="pt-1"
      >
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-foreground/40">0{index + 1}</span>
          <h3 className="text-lg font-medium tracking-tight">{step.title}</h3>
        </div>
        <p className="mt-1.5 max-w-md text-sm text-foreground/60 leading-relaxed">{step.body}</p>
      </motion.div>
    </div>
  );
}

export function WebIndexPipeline() {
  return (
    <section className="border-b hairline">
      <div className="container-x py-14 md:py-20">
        <div className="flex items-baseline justify-between border-b hairline pb-4">
          <h2 className="text-sm tracking-[0.18em] uppercase text-foreground/65">
            How a page becomes an answer
          </h2>
          <span className="font-mono text-xs text-foreground/65">
            seen → read → understood → answerable
          </span>
        </div>

        <div className="mt-8 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <h3 className="display-serif text-3xl md:text-4xl leading-tight">
              Four steps, and no waiting in between.
            </h3>
            <p className="mt-5 text-foreground/70 leading-relaxed">
              A traditional index rebuilds on a schedule, so what it knows is
              always a little out of date. Here the last step happens the moment
              the first one does &mdash; a page seen now is answerable now.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            {STEPS.map((s, i) => (
              <Step key={s.title} step={s} index={i} last={i === STEPS.length - 1} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
